package org.teddapp.project.sync

import com.tedd.db.TeddDatabase

/**
 * Le trousseau dans le moteur de synchronisation. Tout ce qui voyage est déjà chiffré
 * sur l'appareil : la clé du trousseau, enveloppée par la phrase secrète, et chaque
 * entrée, scellée par cette clé. Le serveur garde, il ne lit pas.
 */
internal object VaultSyncOps {

    fun dirtyQuery(entity: EntityKind, db: TeddDatabase): app.cash.sqldelight.Query<*> = when (entity) {
        EntityKind.Keychain -> db.keychainQueries.selectDirty()
        EntityKind.KeychainItem -> db.keychainItemQueries.selectDirty()
        else -> notMine(entity)
    }

    fun selectDirty(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.Keychain -> db.keychainQueries.selectDirty().executeAsList().map { keychainRow(it) }
        EntityKind.KeychainItem -> db.keychainItemQueries.selectDirty().executeAsList().map { itemRow(it) }
        else -> notMine(entity)
    }

    fun selectAll(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.Keychain -> db.keychainQueries.selectAll().executeAsList().map { keychainRow(it) }
        EntityKind.KeychainItem -> db.keychainItemQueries.selectAll().executeAsList().map { itemRow(it) }
        else -> notMine(entity)
    }

    fun markSynced(entity: EntityKind, db: TeddDatabase, id: String, updatedAt: Long, syncedAt: Long) {
        when (entity) {
            EntityKind.Keychain -> db.keychainQueries.markSynced(syncedAt, id, updatedAt)
            EntityKind.KeychainItem -> db.keychainItemQueries.markSynced(syncedAt, id, updatedAt)
            else -> notMine(entity)
        }
    }

    fun selectByIdAnyState(entity: EntityKind, db: TeddDatabase, id: String): LocalSnapshot? = when (entity) {
        EntityKind.Keychain -> db.keychainQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        EntityKind.KeychainItem -> db.keychainItemQueries.selectByIdAnyState(id).executeAsOneOrNull()?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        else -> notMine(entity)
    }

    fun insertFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.Keychain -> db.keychainQueries.insert(
                row.id,
                p.str("name") ?: "Trousseau",
                p.str("icon") ?: "lock",
                p.str("color") ?: "#22508A",
                p.str("wrapped_key") ?: "",
                p.str("kdf") ?: "{}",
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            EntityKind.KeychainItem -> db.keychainItemQueries.insert(
                row.id,
                p.str("keychain_id") ?: "",
                p.str("payload") ?: "",
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            else -> notMine(entity)
        }
    }

    fun updateFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.Keychain -> db.keychainQueries.updateFromRemote(
                p.str("name") ?: "Trousseau",
                p.str("icon") ?: "lock",
                p.str("color") ?: "#22508A",
                p.str("wrapped_key") ?: "",
                p.str("kdf") ?: "{}",
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            EntityKind.KeychainItem -> db.keychainItemQueries.updateFromRemote(
                p.str("keychain_id") ?: "",
                p.str("payload") ?: "",
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            else -> notMine(entity)
        }
    }

    fun softDeleteFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow, deletedAt: Long) {
        when (entity) {
            EntityKind.Keychain -> db.keychainQueries.softDelete(deletedAt, row.updatedAt, row.id)
            EntityKind.KeychainItem -> db.keychainItemQueries.softDelete(deletedAt, row.updatedAt, row.id)
            else -> notMine(entity)
        }
    }

    fun restoreIfDeleted(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        when (entity) {
            EntityKind.Keychain -> db.keychainQueries.restore(row.updatedAt, row.id)
            EntityKind.KeychainItem -> db.keychainItemQueries.restore(row.updatedAt, row.id)
            else -> notMine(entity)
        }
    }

    private fun keychainRow(it: com.tedd.db.Keychain) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "name" to it.name,
        "icon" to it.icon,
        "color" to it.color,
        "wrapped_key" to it.wrapped_key,
        "kdf" to it.kdf,
        "order_index" to it.order_index,
        "created_at" to it.created_at,
    ))

    private fun itemRow(it: com.tedd.db.Keychain_item) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "keychain_id" to it.keychain_id,
        "payload" to it.payload,
        "order_index" to it.order_index,
        "created_at" to it.created_at,
    ))

    private fun notMine(entity: EntityKind): Nothing = error("${entity.tableName} n'est pas une entité du trousseau")
}
